import { defineWidgetSettings, type WidgetConfigProperty } from "@nexus/widget-sdk";
import { ANIMATE_CHART_CONFIG, ANIMATE_VALUE_CONFIG, MISSING_DATA_CONFIG } from "@nexus/widget-kit";

const SERIES = [
  {
    key: "showDetected",
    labels: "showDetectedLabels",
    title: "Detected bars",
    values: "Detected values",
    description:
      "Named Distinct IPs or Distinct domains when the period filter uses the blocking window",
    shown: true,
    labelled: false,
  },
  {
    key: "showSafe",
    labels: "showSafeLabels",
    title: "Safe to block line",
    values: "Safe to block values",
    shown: true,
    labelled: true,
  },
  {
    key: "showApproved",
    labels: "showApprovedLabels",
    title: "Regulator approved line",
    values: "Regulator approved values",
    description: "Regulator-approved targets, whatever their safety",
    shown: false,
    labelled: false,
  },
] as const;

const seriesProperties = Object.fromEntries(
  SERIES.flatMap((series, index) => [
    [
      series.key,
      {
        type: "boolean",
        title: series.title,
        ...("description" in series ? { description: series.description } : {}),
        default: series.shown,
        "x-ui": { category: "display", group: "show", order: 30 + index },
      } satisfies WidgetConfigProperty,
    ],
    [
      series.labels,
      {
        type: "boolean",
        title: series.values,
        description: "Value labels on the chart",
        default: series.labelled,
        "x-ui": {
          category: "display",
          group: "show",
          order: 40 + index,
          visibleIf: { [series.key]: true },
        },
      } satisfies WidgetConfigProperty,
    ],
  ]),
);

/** What an author sets on the IP blocking trend. */
export const settings = defineWidgetSettings({
  id: "blocking-trend",
  extends: [MISSING_DATA_CONFIG, ANIMATE_VALUE_CONFIG, ANIMATE_CHART_CONFIG],
  properties: {
    showDetails: {
      type: "boolean",
      title: "Summary and data note",
      description: "Shown around the chart",
      default: false,
      "x-ui": { category: "display", group: "show", order: 20, summary: "Summary" },
    },
    showConfirmed: {
      type: "boolean",
      title: "Confirmed blockings",
      description: "Confirmed IPs as a third series",
      default: false,
      "x-ui": { category: "display", group: "show", order: 21 },
    },
    ...seriesProperties,
  },
  // The chart keeps at least one series: the last one shown cannot be turned off.
  overlay: ({ config }) => {
    const shown = SERIES.filter((series) =>
      typeof config[series.key] === "boolean" ? config[series.key] : series.shown,
    );
    return shown.length === 1 ? { disabled: [shown[0].key] } : {};
  },
});
